import { InputAdornment, InputBase, InputBaseProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { ChangeEvent, ReactNode } from "react";
import { useNumberInputValue } from "@sinch/components/form/hooks/useNumberInputValue";

interface PercentageInputProps extends Omit<InputBaseProps, "value" | "onChange" | "onBlur" | "readOnly"> {
  value: number | null;
  onChange: (val: number | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  adornment?: ReactNode;
  readOnly?: boolean;
}

export const PercentageInput = ({ value, onChange, onBlur, adornment, readOnly, ...props }: PercentageInputProps) => {
  const ref = React.useRef<HTMLInputElement>(null);
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange?.(parseFloat(e.target.value) / 100);
  const {
    toClosestLimit,
    toNumber,
    localizedValue: numberValue,
  } = useNumberInputValue({
    value: (value as number) * 100,
    onChange: handleChange,
    min: props.inputProps?.min,
    max: props.inputProps?.max,
  });

  return (
    <PercentageInputBase
      endAdornment={<InputAdornment position="end">{adornment}</InputAdornment>}
      error={props.error}
      fullWidth
      inputRef={ref}
      {...props}
      onBlur={(e) => {
        onBlur?.(e);
        e.target.value = toClosestLimit(e.target.value);
        handleChange?.(e);
      }}
      onChange={(e) => {
        e.target.value = toNumber(e.target.value);
        handleChange?.(e);
      }}
      onFocus={() => ref.current?.select()}
      readOnly={readOnly}
      type="text"
      value={numberValue}
    />
  );
};

const PercentageInputBase = styled(InputBase)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  height: "100%",
  "& .MuiInputBase-input": {
    textAlign: "right",
  },
}));
